const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://dnxdqasmzdzuhqflwxpo.supabase.co';
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 'sb_publishable_5nGCHxqax6NGzlzHe_BmYA_heezxVP4';

const SESSION_KEY = 'mf_admin_session';

export type AdminSession = {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: {
    id: string;
    email?: string;
  };
};

export type AdminProfile = {
  user_id: string;
  role: 'owner' | 'admin' | 'editor';
  display_name: string | null;
};

export async function signInAdmin(email: string, password: string): Promise<AdminSession> {
  const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  const payload = await response.json() as AdminSession & { error_description?: string; msg?: string };

  if (!response.ok || !payload.access_token) {
    throw new Error(payload.error_description || payload.msg || 'Unable to sign in.');
  }

  const profile = await getAdminProfile(payload.access_token, payload.user.id);
  if (!profile) {
    throw new Error('This account is not approved for studio administration.');
  }

  localStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  return payload;
}

export async function getAdminProfile(accessToken: string, userId: string): Promise<AdminProfile | null> {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/admin_profiles?user_id=eq.${encodeURIComponent(userId)}&select=user_id,role,display_name`, {
    headers: {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });

  if (!response.ok) return null;
  const profiles = await response.json() as AdminProfile[];
  return profiles[0] ?? null;
}

export function getStoredAdminSession(): AdminSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as AdminSession;
    if (!session.access_token || !session.user?.id) return null;
    if (session.expires_at && session.expires_at * 1000 <= Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function signOutAdmin() {
  localStorage.removeItem(SESSION_KEY);
}

export const adminApiConfig = {
  url: SUPABASE_URL,
  publishableKey: SUPABASE_PUBLISHABLE_KEY,
};